/**
 * The beat a context menu was opened at (W2-11 batch-tools; DESIGN.md X15-14 "Context menu: paste at beat, markers,
 * range start/end").
 *
 * The lane publishes the pointer as the editor's hover (`lanePoint` with its beat, or `note`). When the user
 * right-clicks, that hover still describes the clicked point, but the lane clears it as soon as the pointer leaves
 * for the menu. A capture-phase `contextmenu` listener on the window (installed by the module's `init`, so it runs
 * before any surface handles the event) takes a snapshot first, together with the click position:
 *  - the hovered note's beat, or the hovered lane beat on the snap grid;
 *  - else, for a click inside a timeline column that publishes no hover (motion columns, the time gutter), the beat
 *    under the pointer on the timeline's shared time axis (`AppServices.timeline.axis`, snapped to its grid). The
 *    column body is found through the host's DOM (a `[data-tl-col]` slot whose canvases start at the axis' y = 0);
 *    when that structure is not there, nothing is snapshotted.
 * A command run from that menu (source 'context', trigger event at the same position) acts at the snapshot; any
 * other run, and a right-click where no beat could be read, falls back to the playhead.
 * The contracted way (the surface that opens the menu passes its beat) is CR-W2-11-6.
 */

import type { AppServices, CommandContext } from '../../app/registry/types';
import { beatFromF64Snapped } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { DocStore } from '../../state/types';
import { playheadBeat, snapDiv } from './batchContext';

interface Snapshot {
  readonly store: DocStore;
  readonly beat: Beat;
  readonly x: number;
  readonly y: number;
}

let snapshot: Snapshot | null = null;

/** The beat under the pointer as the editor's hover describes it (null when nothing is hovered). */
export function hoverBeat(s: Pick<AppServices, 'store' | 'editor'>): Beat | null {
  const store = s.store;
  if (store === null) return null;
  const h = s.editor.getState().hover;
  if (h === null) return null;
  if (h.kind === 'note') return store.doc.notes.get(h.note)?.t ?? null;
  if (!Number.isFinite(h.beat)) return null;
  try {
    return beatFromF64Snapped(Math.max(0, h.beat), snapDiv(s));
  } catch {
    return null;
  }
}

/**
 * The beat under client Y in the timeline column that contains `target`, on the timeline's time axis (null when the
 * target is not inside a column body, or no timeline is mounted).
 */
export function columnBeat(s: Pick<AppServices, 'timeline'>, target: EventTarget | null, clientY: number): Beat | null {
  const host = s.timeline;
  if (host === null || typeof Element === 'undefined' || !(target instanceof Element)) return null;
  const canvas = target.closest('[data-tl-col]')?.querySelector('canvas[data-layer]') ?? null;
  if (canvas === null) return null;
  const axis = host.axis;
  const y = clientY - canvas.getBoundingClientRect().top;
  if (!(y >= 0 && y <= axis.heightPx)) return null;
  const beat = axis.beatAtY(y);
  if (!Number.isFinite(beat)) return null;
  try {
    return axis.snap(Math.max(0, beat));
  } catch {
    return null;
  }
}

/**
 * Remembers the beat for the context menu opening at (x, y): the hovered one, else the one under the pointer in a
 * timeline column; clears the snapshot when there is neither.
 */
export function snapshotContextBeat(
  s: Pick<AppServices, 'store' | 'editor' | 'timeline'>,
  x: number,
  y: number,
  target: EventTarget | null = null,
): void {
  const beat = hoverBeat(s) ?? columnBeat(s, target, y);
  snapshot = beat === null || s.store === null ? null : { store: s.store, beat, x, y };
}

/** Forgets the snapshot (tests, module disposal). */
export function clearContextBeat(): void {
  snapshot = null;
}

/** Installs the window listener; returns its disposer. */
export function installContextBeat(s: Pick<AppServices, 'store' | 'editor' | 'timeline'>): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const onContext = (e: MouseEvent): void => {
    // A menu opened from the keyboard (Menu key, Shift+F10) reports (0, 0): it acts at the playhead.
    if (e.clientX === 0 && e.clientY === 0) clearContextBeat();
    else snapshotContextBeat(s, e.clientX, e.clientY, e.target);
  };
  window.addEventListener('contextmenu', onContext, true);
  return () => {
    window.removeEventListener('contextmenu', onContext, true);
    snapshot = null;
  };
}

/** The right-clicked beat when the command runs from the context menu that snapshot belongs to, else null. */
export function contextBeat(ctx: Pick<CommandContext, 'store' | 'source' | 'event'>): Beat | null {
  const snap = snapshot;
  if (ctx.source !== 'context' || snap === null || snap.store !== ctx.store) return null;
  const e = ctx.event;
  if (e !== undefined && 'clientX' in e && (e.clientX !== snap.x || e.clientY !== snap.y)) return null;
  return snap.beat;
}

/** Where a "here" command acts: the right-clicked beat from a context menu, else the playhead. */
export function actionBeat(ctx: CommandContext): Beat {
  return contextBeat(ctx) ?? playheadBeat(ctx);
}
